import { useApolloClient } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import InfoIcon from "@mui/icons-material/Info";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { AutoCompleteMenu, FileLink, SkeletonLoader, UploadInput } from "@/atoms";
import { FormContainer, ReleaseVideoIsrcForm, ReleaseVideoThumbnail } from "@/components";
import {
    constantsData,
    fileData,
    identifiersData,
    inputData,
    namesData,
    permissionsData,
    releaseVideoFormData,
} from "@/data";
import { getTrackQuery } from "@/graph";
import { useCreateVideo, useDeleteVideo, useEditVideo, useUploadFile } from "@/hooks";
import { useReleaseVideoSchema } from "@/schemas";
import { StyledTextField } from "@/styles";
import { canDo, getSubstringHandler, renderFormControllerHandler, setUploadInputPreviousValueHandler } from "@/utils";

const thumbnailGuidelinesKeys = ["format", "dimensions", "maxSize"];

const ReleaseVideoForm = ({ setTrackId, trackId, trackLoading, values }) => {
    const [addVideoLoading, setAddVideoLoading] = useState(false);

    const previousVideosCount = useRef(null);

    const { t } = useTranslation();

    const { user } = useAuth0();

    const client = useApolloClient();

    const schema = useReleaseVideoSchema();

    const tracksList = useSelector((state) => state.releasesReducer)?.releaseTracks;

    const selections = useSelector((state) => state.selectionsReducer);

    const { id: releaseId } = useParams();

    const { deleteVideo } = useDeleteVideo(trackId);

    const { filesData, filesLoading, setFilesData, uploadFile } = useUploadFile(trackId, parseInt(releaseId));

    const { createVideo } = useCreateVideo(trackId, parseInt(releaseId));

    const { editVideoThumbnail } = useEditVideo(trackId);

    const formMethods = useForm({
        // the isrc needs an explicit default to stay a controlled input
        defaultValues: { ...values, [inputData.names.isrc]: "" },
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const {
        control,
        formState: { errors },
        setError,
        setValue,
        watch,
    } = formMethods;

    const {
        mediaCreate: mediaCreatePermission,
        mediaDelete: mediaDeletePermission,
        mediaEdit: mediaEditPermission,
        mediaRead: mediaReadPermission,
    } = permissionsData.permissions.keys;

    const canAddVideo = canDo(user, [mediaCreatePermission, mediaReadPermission]);

    // the thumbnail is attached to the created video with an update, so it also needs media:edit
    const canUploadThumbnail = canDo(user, [mediaCreatePermission, mediaEditPermission]);

    const { file: fileInputName, isrc: isrcInputName, track: trackInputName, type: typeInputName } = inputData.names;

    const { video: videoFileName, videoThumbnail: videoThumbnailFileName } = fileData.names;

    const { button: buttonId, input: inputId } = identifiersData.ids;

    const { add: addButtonName, info: infoButtonName } = namesData.buttons;

    const isUploading = Object.values(filesLoading || {}).some(Boolean);

    const tracksOptions = tracksList?.map(({ id, title }) => ({
        label: title,
        value: id,
    }));

    const clearUploadedFilesHandler = (names = [videoFileName, videoThumbnailFileName]) => {
        names.forEach((name) => {
            setFilesData((prev) => ({
                ...prev,
                [name]: null,
            }));

            setValue(name, null);

            // the video upload input keeps the default file input id
            const fileInput = document.getElementById(
                name === videoFileName ? `${fileInputName}_${inputId}` : `${name}_${inputId}`,
            );

            if (fileInput) fileInput.value = "";
        });
    };

    const createVideoErrorHandler = (err) => {
        // the ISRC checks run before the backend creates anything, so the uploaded file can be reused
        if ([constantsData.duplicatedVideoIsrc, constantsData.duplicatedTrackIsrc].includes(err.message)) {
            setError(isrcInputName, {
                message:
                    err.message === constantsData.duplicatedVideoIsrc
                        ? t("messages.videoMsgs.duplicatedIsrc")
                        : err.message,
            });

            return;
        }

        // any later failure may have created the video with this file already, so the file cannot be
        // reused and the track is refetched to show that video. useCreateVideo already alerts the error
        clearUploadedFilesHandler([videoFileName]);

        client.refetchQueries({ include: [getTrackQuery] });
    };

    // the ids come from filesData, which only holds successful uploads, because the upload input's field
    // also receives the picked file's path, even for a rejected type or a failed upload
    const uploadedVideoFileId = filesData?.[videoFileName]?.id;

    const uploadedThumbnailImageId = filesData?.[videoThumbnailFileName]?.imageId;

    // the uploaded video file id is kept after a rejected ISRC, so it only needs fixing before adding again
    const addVideoHandler = async (formValues) => {
        // pressing enter in the ISRC field submits even while the add button is disabled
        if (!uploadedVideoFileId || isUploading || addVideoLoading) return;

        setAddVideoLoading(true);

        const createdVideo = await createVideo(
            uploadedVideoFileId,
            formValues[typeInputName],
            null,
            formValues[isrcInputName],
            createVideoErrorHandler,
        );

        if (createdVideo?.id) {
            if (uploadedThumbnailImageId) {
                await editVideoThumbnail(createdVideo.id, uploadedThumbnailImageId, { withoutAlert: true });
            }

            clearUploadedFilesHandler();
        }

        setAddVideoLoading(false);
    };

    const renderFormControllerChildrenHandler = (field, label, name, type, _, selectionKey) => {
        if (type === inputData.types.text) {
            return (
                <StyledTextField
                    {...field}
                    error={!!errors[name]?.message}
                    helperText={errors[name]?.message}
                    id={`${name}_${identifiersData.ids.input}`}
                    label={t(`labels.${label}`)}
                    variant="outlined"
                    fullWidth
                />
            );
        }

        if (type === inputData.types.file) {
            const isThumbnail = name === videoThumbnailFileName;

            return (
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: 1,
                        mb: 1,
                    }}
                >
                    <Box>
                        <UploadInput
                            color={errors[name]?.message ? "error" : "primary"}
                            error={errors[name]?.message}
                            field={field}
                            hasError={!!errors[name]?.message}
                            id={isThumbnail ? name : undefined}
                            label={getSubstringHandler(filesData?.[name]?.name, 20) || t(`labels.${label}`)}
                            loading={filesLoading?.[name]}
                            accept={
                                isThumbnail
                                    ? fileData.acceptedExtensions.videoThumbnail
                                    : fileData.acceptedExtensions.video
                            }
                            disabled={isThumbnail ? !canUploadThumbnail : !canAddVideo}
                            onChange={(e) => {
                                uploadFile(
                                    e.target.files[0],
                                    formMethods,
                                    name,
                                    null,
                                    isThumbnail ? undefined : { deferVideoCreation: true },
                                );
                            }}
                        />
                    </Box>
                    {isThumbnail && (
                        <Tooltip
                            title={
                                <Box>
                                    <Typography sx={{ fontWeight: "bold" }} variant="subtitle2">
                                        {t("videos.thumbnailGuidelines.title")}
                                    </Typography>
                                    {thumbnailGuidelinesKeys.map((key) => (
                                        <Typography key={key} sx={{ display: "block" }} variant="caption">
                                            {t(`videos.thumbnailGuidelines.${key}`)}
                                        </Typography>
                                    ))}
                                </Box>
                            }
                        >
                            <IconButton
                                aria-label={t("videos.thumbnailGuidelines.title")}
                                id={`${name}_${infoButtonName}_${buttonId}`}
                                size="small"
                            >
                                <InfoIcon
                                    fontSize="small"
                                    sx={{
                                        color: "grey.500",
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        }

        return (
            <AutoCompleteMenu
                errors={errors}
                getRenderedOption={(option) => option.label}
                label={label}
                name={name}
                optionLabel={(option) => option.label}
                options={selectionKey === "tracks" ? tracksOptions : selections?.[selectionKey]}
                onChange={(__, option) => {
                    if (option) field.onChange(option.value);
                    else field.onChange("");
                }}
            />
        );
    };

    useEffect(
        () => {
            setTrackId(tracksList?.find(({ id }) => id === watch(trackInputName))?.id);
        },
        [watch(trackInputName)], // eslint-disable-line
    );

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, videoFileName);
        },
        [watch(videoFileName)], // eslint-disable-line
    );

    // a newly created video means the upload succeeded, so clear the consumed
    // ISRC to keep the next upload on the same track from reusing it
    useEffect(
        () => {
            const videosCount = values?.videos?.length ?? 0;

            if (previousVideosCount.current !== null && videosCount > previousVideosCount.current) {
                setValue(isrcInputName, "");
            }

            previousVideosCount.current = videosCount;
        },
        [values?.videos?.length], // eslint-disable-line
    );

    return (
        <FormContainer
            formMethods={formMethods}
            hasSubmitButton={false}
            // the promise is not returned on purpose, otherwise FormContainer resets the form
            // after submitting and wipes the ISRC error set when creating the video fails
            onSubmitForm={(formValues) => {
                addVideoHandler(formValues);
            }}
            data={
                <>
                    {renderFormControllerHandler(control, releaseVideoFormData, renderFormControllerChildrenHandler)}
                    <Grid
                        size={{
                            md: "auto",
                            xs: 12,
                        }}
                        sx={{ alignSelf: "center", mb: 1 }}
                    >
                        <LoadingButton
                            id={`${addButtonName}_${buttonId}`}
                            loading={addVideoLoading}
                            type="submit"
                            variant="contained"
                            disabled={
                                !watch(trackInputName) ||
                                !watch(typeInputName) ||
                                !watch(isrcInputName) ||
                                !!errors[isrcInputName]?.message ||
                                !uploadedVideoFileId ||
                                isUploading ||
                                !canAddVideo
                            }
                        >
                            {t("actions.add")}
                        </LoadingButton>
                    </Grid>
                </>
            }
            otherData={
                <Grid spacing={2} container>
                    <Grid
                        size={{
                            md: 8,
                            xs: 12,
                        }}
                    >
                        {trackLoading ? (
                            <SkeletonLoader />
                        ) : (
                            values?.videos?.length > 0 &&
                            values?.videos
                                ?.filter(({ release }) => release?.id === parseInt(releaseId))
                                .map(
                                    (
                                        {
                                            downloadUri,
                                            id,
                                            image,
                                            imageId,
                                            isrc,
                                            name: fileName,
                                            thumbnail,
                                            thumbnailId,
                                            type: fileType,
                                        },
                                        index,
                                    ) => (
                                        <FileLink
                                            key={id}
                                            link={downloadUri}
                                            name={fileName}
                                            otherData={
                                                <Box
                                                    sx={{
                                                        alignItems: "center",
                                                        display: "flex",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography variant="caption">{fileType?.name}</Typography>
                                                    <ReleaseVideoIsrcForm isrc={isrc} trackId={trackId} videoId={id} />
                                                </Box>
                                            }
                                            rowId={index + 1}
                                            startData={
                                                <ReleaseVideoThumbnail
                                                    image={image}
                                                    imageId={imageId}
                                                    rowId={index + 1}
                                                    thumbnail={thumbnail}
                                                    thumbnailId={thumbnailId}
                                                    trackId={trackId}
                                                    videoId={id}
                                                />
                                            }
                                            deleteAction={() => {
                                                document.getElementById(
                                                    `${fileInputName}_${identifiersData.ids.input}`,
                                                ).value = ""; //eslint-disable-line

                                                deleteVideo(id);
                                            }}
                                            hasDelete={canDo(user, [mediaDeletePermission])}
                                            hasDownload
                                        />
                                    ),
                                )
                        )}
                    </Grid>
                </Grid>
            }
        />
    );
};

export default ReleaseVideoForm;

ReleaseVideoForm.propTypes = {
    setTrackId: PropTypes.func,
    trackId: PropTypes.number,
    trackLoading: PropTypes.bool,
    values: PropTypes.object,
};
